/**
 * @module surveySessionGuard
 * @description Cross-tab survey conflict detection using the browser's BroadcastChannel API.
 * Ensures only one active anonymous survey session runs at a time without storing any tokens.
 */

const CHANNEL_NAME = 'sl_survey_active_session_channel';

let activeChannel = null;
let currentActiveSurvey = null;

/**
 * Registers the current tab as an active survey session.
 * Listens for pings from other survey tabs and responds if active.
 *
 * @param {object} surveyInfo - Metadata of current survey (inviteId, surveyId, surveyTitle, etc.)
 */
export const registerActiveSurveyTab = (surveyInfo) => {
  if (typeof window === 'undefined') return;

  currentActiveSurvey = { ...surveyInfo, tabRegisteredAt: Date.now() };

  try {
    sessionStorage.setItem('SL_ACTIVE_SURVEY_INFO', JSON.stringify(currentActiveSurvey));
  } catch (e) {}

  try {
    if ('BroadcastChannel' in window) {
      if (!activeChannel) {
        activeChannel = new BroadcastChannel(CHANNEL_NAME);
      }

      activeChannel.onmessage = (event) => {
        if (event.data?.type === 'PING_ACTIVE_SURVEY') {
          console.log('[surveySessionGuard] Active tab received PING, responding with:', currentActiveSurvey);
          if (activeChannel && currentActiveSurvey) {
            activeChannel.postMessage({
              type: 'PONG_ACTIVE_SURVEY',
              activeSurvey: currentActiveSurvey,
            });
          }
        }
      };
    }
  } catch (err) {
    console.warn('[surveySessionGuard] BroadcastChannel register error:', err);
  }
};

/**
 * Unregisters the active survey tab when completed or explicitly reset.
 */
export const unregisterActiveSurveyTab = () => {
  currentActiveSurvey = null;
  try {
    sessionStorage.removeItem('SL_ACTIVE_SURVEY_INFO');
  } catch (e) {}

  if (activeChannel) {
    try {
      activeChannel.close();
    } catch (e) {}
    activeChannel = null;
  }
};

// Auto-resume active listener if this tab already had an active survey session
if (typeof window !== 'undefined') {
  try {
    const saved = sessionStorage.getItem('SL_ACTIVE_SURVEY_INFO');
    if (saved) {
      const parsed = JSON.parse(saved);
      registerActiveSurveyTab(parsed);
    }
  } catch (e) {}
}

/**
 * Pings other browser tabs to see if a DIFFERENT survey is actively open.
 * Resolves with { hasConflict: true, activeSurvey } if another survey responds.
 * Resolves with { hasConflict: false } if no other survey responds within 300ms.
 *
 * @param {object} incomingSurveyInfo - Information about the incoming survey link
 * @returns {Promise<{ hasConflict: boolean, activeSurvey?: object }>}
 */
export const checkActiveSurveyTab = (incomingSurveyInfo) => {
  console.log('[surveySessionGuard] Checking active survey tab for incoming:', incomingSurveyInfo);
  if (typeof window === 'undefined' || !('BroadcastChannel' in window)) {
    return Promise.resolve({ hasConflict: false });
  }

  return new Promise((resolve) => {
    let checkChannel = null;
    let timer = null;

    try {
      checkChannel = new BroadcastChannel(CHANNEL_NAME);
    } catch (err) {
      return resolve({ hasConflict: false });
    }

    const cleanup = () => {
      if (timer) clearTimeout(timer);
      if (checkChannel) {
        try {
          checkChannel.close();
        } catch (e) {}
        checkChannel = null;
      }
    };

    // Timeout: if no tab responds within 300ms, assume no other survey tab is open
    timer = setTimeout(() => {
      cleanup();
      console.log('[surveySessionGuard] No conflicting tab responded within timeout.');
      resolve({ hasConflict: false });
    }, 300);

    checkChannel.onmessage = (event) => {
      if (event.data?.type === 'PONG_ACTIVE_SURVEY') {
        const active = event.data.activeSurvey;
        if (active) {
          let isSameSurvey = false;

          // If both have inviteId, they MUST match to be the same survey
          if (incomingSurveyInfo?.inviteId && active.inviteId) {
            isSameSurvey = String(incomingSurveyInfo.inviteId).trim() === String(active.inviteId).trim();
          } else if (incomingSurveyInfo?.surveyId && active.surveyId) {
            // Check surveyId + queueId/userId
            const sameSurvey = String(incomingSurveyInfo.surveyId).trim() === String(active.surveyId).trim();
            const sameQueue = incomingSurveyInfo.queueId && active.queueId
              ? String(incomingSurveyInfo.queueId).trim() === String(active.queueId).trim()
              : true;
            const sameUser = incomingSurveyInfo.userId && active.userId
              ? String(incomingSurveyInfo.userId).trim() === String(active.userId).trim()
              : true;
            isSameSurvey = sameSurvey && sameQueue && sameUser;
          }

          console.log(
            '[surveySessionGuard] PONG received. Active survey:', active,
            'Incoming survey:', incomingSurveyInfo,
            'isSameSurvey:', isSameSurvey
          );

          if (!isSameSurvey) {
            cleanup();
            resolve({
              hasConflict: true,
              activeSurvey: active,
            });
          }
        }
      }
    };

    // Send PING across the channel
    try {
      checkChannel.postMessage({
        type: 'PING_ACTIVE_SURVEY',
        incomingSurvey: incomingSurveyInfo,
      });
    } catch (err) {
      cleanup();
      resolve({ hasConflict: false });
    }
  });
};
